// 原価パターン(1〜9)と通貨単位の判定・entrydata への反映をまとめる。
// 加工指示・品番マスタで同一実装のため共有する(通信と state 更新は呼び出し側に残す)。
export function createGenkaPatternResolver(deps) {
    const {
        isEmpty,
        DicVal,
        findEntryValue,
        normalizeCodeValue,
        resolveEntryColumnId,
        findColumnInfoByCandidates,
        parseBoolLike,
        normalizeColOptionsToStrings,
        getController,
        getEdittingCode,
        candidates
    } = deps;

    const {
        ssdNo: ssdNoCandidates,
        kaigaiKbn: kaigaiKbnCandidates,
        brandKbn: brandKbnCandidates,
        yushoMushoKbn: yushoMushoKbnCandidates,
        shohinKbn: shohinKbnCandidates,
        oppiCd: oppiCdCandidates,
        kakouSakiKbn: kakouSakiKbnCandidates,
        kakouSakiCd: kakouSakiCdCandidates,
        kakouSakiSubCd: kakouSakiSubCdCandidates,
        supplierCurrency: supplierCurrencyCandidates,
        headerCurrency: headerCurrencyCandidates,
        headerCurrencyLayoutType: headerCurrencyLayoutTypeCandidates,
        rate: rateCandidates
    } = candidates;

    function genkaPatternLabelByNo(no) {
        const labels = {
            1: '1:国内無償',
            2: '2:国内有償',
            3: '3:国内商品仕入',
            4: '4:海外無償（円貨）',
            5: '5:海外無償（外貨）',
            6: '6:海外有償（円貨）',
            7: '7:海外有償（外貨）',
            8: '8:海外商品仕入（円貨）',
            9: '9:海外商品仕入（外貨）'
        };
        return labels[Number(no)] || '';
    }
    function genkaCurrencyDisplayLabel(value) {
        const code = String(value ?? '').trim().toUpperCase();
        if (code === '') return '';
        return code === 'JPY' ? '円貨' : '外貨';
    }
    function isJpyCurrencyValue(value) {
        return String(value ?? '').trim().toUpperCase() === 'JPY';
    }
    // 仕入先通貨が円貨なら、原価ヘッダの通貨も円貨に固定する。
    function shouldForceGenkaCurrencyJpy(entryObj) {
        if (isEmpty(entryObj)) return false;
        const supplierCurrency = findEntryValue(entryObj, supplierCurrencyCandidates);
        return isJpyCurrencyValue(supplierCurrency);
    }
    // サーバ照会前の暫定表示用に、区分の組み合わせからパターン番号を推定する。
    function deriveGenkaPatternFromEntry(entryObj) {
        if (isEmpty(entryObj)) return undefined;
        const kaigai = normalizeCodeValue(findEntryValue(entryObj, kaigaiKbnCandidates));
        const brand = normalizeCodeValue(findEntryValue(entryObj, brandKbnCandidates));
        const yusho = normalizeCodeValue(findEntryValue(entryObj, yushoMushoKbnCandidates));
        const shohin = normalizeCodeValue(findEntryValue(entryObj, shohinKbnCandidates));
        const isJPY = isJpyCurrencyValue(findEntryValue(entryObj, supplierCurrencyCandidates));
        const isShohin01 = shohin === '01' || shohin === '1';
        const isShohin03 = shohin === '03' || shohin === '3';
        const isShohin04 = shohin === '04' || shohin === '4';
        const isShohin010304 = isShohin01 || isShohin03 || isShohin04;
        if (kaigai === '1' && brand === '0' && yusho === '2' && isJPY && isShohin01) return 1;
        if (kaigai === '1' && brand === '0' && yusho === '1' && isJPY && isShohin03) return 2;
        if (kaigai === '1' && brand === '1' && yusho === '2' && isJPY && isShohin010304) return 3;
        if (kaigai === '2' && brand === '0' && yusho === '2' && isJPY) return 4;
        if (kaigai === '2' && brand === '0' && yusho === '2' && !isJPY) return 5;
        if (kaigai === '2' && brand === '0' && yusho === '1' && isJPY) return 6;
        if (kaigai === '2' && brand === '0' && yusho === '1' && !isJPY) return 7;
        if (kaigai === '2' && brand === '1' && yusho === '2' && isJPY) return 8;
        if (kaigai === '2' && brand === '1' && yusho === '2' && !isJPY) return 9;
        return undefined;
    }
    function normalizeGenkaPatternNo(value) {
        const num = Number(value);
        if (!Number.isFinite(num)) return 0;
        return num;
    }
    function resolveGenkaPatternRequestCode(code, entryObj) {
        const direct = String((typeof code === 'string' || typeof code === 'number') ? code : '').trim();
        if (direct !== '') return direct;
        const fromEntry = String(findEntryValue(entryObj, ssdNoCandidates) ?? '').trim();
        if (fromEntry !== '') return fromEntry;
        return String(getEdittingCode() ?? '').trim();
    }
    function buildGenkaPatternRequest(code, entryObj) {
        const request = { controller: getController(), code: resolveGenkaPatternRequestCode(code, entryObj) };
        const oppiCd = String(findEntryValue(entryObj, oppiCdCandidates) ?? '').trim();
        const kaigaiKbn = String(findEntryValue(entryObj, kaigaiKbnCandidates) ?? '').trim();
        const kakouSakiKbn = String(findEntryValue(entryObj, kakouSakiKbnCandidates) ?? '').trim();
        const kakouSakiCd = String(findEntryValue(entryObj, kakouSakiCdCandidates) ?? '').trim();
        const kakouSakiSubCd = String(findEntryValue(entryObj, kakouSakiSubCdCandidates) ?? '').trim();
        const tsukaTani = String(findEntryValue(entryObj, supplierCurrencyCandidates) ?? '').trim();
        if (oppiCd !== '') request.oppiCd = oppiCd;
        if (kaigaiKbn !== '') request.kaigaiKbn = kaigaiKbn;
        if (kakouSakiKbn !== '') request.kakouSakiKbn = kakouSakiKbn;
        if (kakouSakiCd !== '') request.kakouSakiCd = kakouSakiCd;
        if (kakouSakiSubCd !== '') request.kakouSakiSubCd = kakouSakiSubCd;
        if (tsukaTani !== '') request.tsukaTani = tsukaTani;
        return request;
    }
    function findGenkaCurrencyLayoutMeta() {
        return findColumnInfoByCandidates(headerCurrencyCandidates, headerCurrencyLayoutTypeCandidates)
            || findColumnInfoByCandidates(headerCurrencyCandidates, []);
    }
    function isGenkaCurrencyEditableByLayout() {
        const cinfo = findGenkaCurrencyLayoutMeta();
        if (isEmpty(cinfo)) return false;
        if (parseBoolLike(DicVal(cinfo, 'locked', false), false)) return false;
        if (parseBoolLike(DicVal(cinfo, 'unchange', false), false)) return false;
        if (!parseBoolLike(DicVal(cinfo, 'saveed', true), true)) return false;
        return true;
    }
    function genkaCurrencyOptionsFromLayout() {
        const cinfo = findGenkaCurrencyLayoutMeta();
        if (isEmpty(cinfo)) return [];
        return normalizeColOptionsToStrings(DicVal(cinfo, 'options', []));
    }
    function resolveGenkaCurrencyFromEntry(entryObj) {
        if (shouldForceGenkaCurrencyJpy(entryObj)) return 'JPY';
        return String(findEntryValue(entryObj, headerCurrencyCandidates) ?? '').trim();
    }
    function setGenkaCurrencyToEntry(entryObj, value) {
        if (isEmpty(entryObj)) return false;
        const colId = resolveEntryColumnId(headerCurrencyCandidates, entryObj);
        if (isEmpty(colId)) return false;
        entryObj[colId] = String(value ?? '');
        return true;
    }
    function setGenkaRateToEntry(entryObj, value) {
        if (isEmpty(entryObj)) return false;
        const colId = resolveEntryColumnId(rateCandidates, entryObj);
        if (isEmpty(colId)) return false;
        entryObj[colId] = String(value ?? '');
        return true;
    }
    function applyForcedGenkaJpyIfNeeded(entryObj) {
        if (!shouldForceGenkaCurrencyJpy(entryObj)) return false;
        const changedCurrency = setGenkaCurrencyToEntry(entryObj, 'JPY');
        const changedRate = setGenkaRateToEntry(entryObj, '1');
        return changedCurrency || changedRate;
    }

    return {
        genkaPatternLabelByNo,
        genkaCurrencyDisplayLabel,
        isJpyCurrencyValue,
        shouldForceGenkaCurrencyJpy,
        deriveGenkaPatternFromEntry,
        normalizeGenkaPatternNo,
        resolveGenkaPatternRequestCode,
        buildGenkaPatternRequest,
        findGenkaCurrencyLayoutMeta,
        isGenkaCurrencyEditableByLayout,
        genkaCurrencyOptionsFromLayout,
        resolveGenkaCurrencyFromEntry,
        setGenkaCurrencyToEntry,
        setGenkaRateToEntry,
        applyForcedGenkaJpyIfNeeded
    };
}
